import React, { useState, FormEvent, useEffect } from 'react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { InputTextField } from '@/components';
import { useToastNotification } from '@/hooks';
import { useMutation } from '@tanstack/react-query';

import { faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ModalDetailSelectFavClient from '../Depo20/components/ModalDetailSelectFavClient';

import styles from '../Depo20/Depo20.module.scss';
import { comprobarDocumento } from '../Depo20/services/comprobarDocumento';
import { titularRut } from '../CustomerRegistration/titularRut';

export interface ClientState {
  rut: string;
  nombre: string;
  referencia?: string;
  oid?: number;
}

interface ImportFCLClientFormProps {
  value?: ClientState | undefined;
  formDataLoading?: boolean;
  titleClient?: boolean;
  disabled?: boolean;
  onChange?: (value: ClientState) => void;
  buttonRef?: React.RefObject<HTMLButtonElement>; // Here is the ref prop
  isImport?: boolean;
}

const ImportFCLClientForm: React.FC<ImportFCLClientFormProps> = ({
  value,
  formDataLoading,
  titleClient,
  disabled = false,
  onChange,
  buttonRef,
  isImport = false,
}) => {
  const { t } = useTranslation('importFCL');
  const { showToast } = useToastNotification();

  const [formData, setFormData] = useState<ClientState>(
    value ?? ({} as ClientState)
  );

  const [formErrors, setFormErrors] = useState<ClientState>({
    rut: '',
    nombre: '',
    referencia: '',
  });

  const [isDialogShown, setIsDialogShown] = useState(false);

  const toggleDialog = () => {
    setIsDialogShown(!isDialogShown);
  };

  const { mutate: fetchTitularRut, isPending: fetchingTitular } = useMutation({
    mutationFn: titularRut,
    onSuccess: (response) => {
      if (response?.razonSocial) {
        setFormData((prevState) => ({
          ...prevState,
          nombre: response.razonSocial,
        }));
      }
    },
    onError: (error) => {
      setFormData((prevState) => ({
        ...prevState,
        nombre: '',
      }));
      showToast({
        severity: 'warn',
        detail: error?.message || t('titularNotFound'),
      });
    },
  });

  const handleInputChange = (name: string, inputValue: string) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: inputValue,
    }));
  };

  useEffect(() => {
    if (!onChange) return;
    onChange(formData);
  }, [onChange, formData]);

  const validateForm = async () => {
    const errors: ClientState = { rut: '', nombre: '', referencia: '' };
    let formIsValid = true;

    if (!formData.rut.match(/^\d{12}$/)) {
      errors.rut = t('rutInvalid');
      formIsValid = false;
    }
    const response = await comprobarDocumento({
      documento: formData?.rut,
      tipo: 2,
    });
    if (response === 1) {
      errors.rut =
        'El RUT ingresado pertenece a un titular sin dirección. En el proceso de factura electrónica es necesario que el titular contenga el dato mencionado. Comuníquese con atención al cliente para regularizar la información';
      formIsValid = false;
    }

    if (response === 2) {
      errors.rut = 'El RUT ingresado es inválido';
      formIsValid = false;
    }

    setFormErrors(errors);
    return formIsValid;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  useEffect(() => {
    setFormData(value ?? ({} as ClientState));
  }, [value]);

  useEffect(() => {
    const rutValue = formData.rut?.trim();

    if (rutValue && rutValue.match(/^\d{12}$/)) {
      fetchTitularRut({ rut: rutValue });
    } else if (rutValue && !rutValue.match(/^\d{12}$/)) {
      setFormData((prevState) => ({
        ...prevState,
        nombre: '',
      }));
    }
  }, [formData.rut, fetchTitularRut]);

  const addToFavourites = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const formValid = await validateForm();
    if (formValid) {
      e.preventDefault();

      window.localStorage.setItem(
        `${titleClient ? '' : 'despachante'}rutFavoritoLiquidacion ${value?.oid}`,
        formData.rut
      );
      window.localStorage.setItem(
        `${titleClient ? '' : 'despachante'}nombreFavoritoLiquidacion ${value?.oid}`,
        formData.nombre
      );

      showToast({
        severity: 'success',
        detail: t('favClientAdded'),
      });
    }
  };

  useEffect(() => {
    setFormData((current) => ({
      ...current,
      rut:
        value?.rut ||
        window.localStorage.getItem(
          `${titleClient ? '' : 'despachante'}rutFavoritoLiquidacion ${value?.oid}`
        ) ||
        '',
      nombre:
        value?.nombre ||
        window.localStorage.getItem(
          `${titleClient ? '' : 'despachante'}nombreFavoritoLiquidacion ${value?.oid}`
        ) ||
        '',
    }));
  }, [value, titleClient]);

  return (
    <div className={styles.container}>
      <ModalDetailSelectFavClient
        oid={Number(formData.rut)}
        isDialogShown={isDialogShown}
        toggleDialog={toggleDialog}
      />
      <form onSubmit={handleSubmit} className="p-fluid">
        <div className={styles.clientFormContainer}>
          {titleClient ? (
            <h2 className=" text-sm font-semibold mb-2 ">
              {t('clientSectionTitle')}
            </h2>
          ) : (
            <h2 className=" text-sm font-semibold mb-2 ">
              {t('destSectionTitle')}
            </h2>
          )}
          <InputTextField
            id="rut"
            name="rut"
            value={formData.rut}
            onChange={(e) => handleInputChange('rut', e.target.value)}
            placeholder={t('rutPlaceholder')}
            label={t('rut')}
            error={formErrors.rut}
            disabled={disabled}
          >
            <Button
              onClick={addToFavourites}
              className={`text-blue-800 p-button-outlined align-items-center justify-content-center ml-3 px-3 ${styles.starButton}`}
              severity="danger"
              disabled={disabled}
            >
              <FontAwesomeIcon icon={faStar} />
            </Button>
          </InputTextField>

          <InputTextField
            id="nombre"
            name="nombre"
            value={formData.nombre}
            onChange={(e) => handleInputChange('nombre', e.target.value)}
            placeholder={t('namePlaceholder')}
            label={t('name')}
            error={formErrors.nombre}
            disabled={disabled || formDataLoading || fetchingTitular}
          />
        </div>
        {!isImport && (
          <InputTextField
            id="referencia"
            name="referencia"
            value={formData?.referencia ?? ''}
            onChange={(e) => handleInputChange('referencia', e.target.value)}
            placeholder={t('referencePlaceholder')}
            label={t('reference')}
            error={formErrors.referencia}
            disabled={disabled || formDataLoading}
          />
        )}
        <button type="submit" className="hidden" ref={buttonRef}>
          Hidden button
        </button>
      </form>
    </div>
  );
};

export default ImportFCLClientForm;
